import React, { FunctionComponent, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { ApplicationState } from "../../core/types"
import { WhatsNewState } from "../../data/stores/whats-new/whats-new-types"
import { fetchWhatsNewFeed, setWhatsNewOpen } from "../../data/stores/whats-new/whats-new-actions"
import { NewsFeedItem } from "../../data/model/whats-new-model"

const LS_KEY = "sl_whats_new_last_seen"

function getLastSeen(): number {
    try {
        const v = localStorage.getItem(LS_KEY)
        return v ? parseInt(v, 10) : 0
    } catch {
        return 0
    }
}

function setLastSeen(): void {
    try {
        localStorage.setItem(LS_KEY, String(Date.now()))
    } catch { /* ignore */ }
}

function countNew(items: NewsFeedItem[], lastSeen: number): number {
    if (lastSeen === 0) return 0  // first visit — no badge
    return items.filter(item => new Date(item.publishedAt).getTime() > lastSeen).length
}

const NewBadgeIcon: FunctionComponent = () => (
    <span className="whats-new-btn__new-badge" aria-hidden="true">NEW</span>
)

export const WhatsNewButton: FunctionComponent = () => {
    const dispatch = useDispatch()
    const { items, loading, isOpen } = useSelector<ApplicationState, WhatsNewState>(
        state => state.whatsNew
    )

    useEffect(() => {
        dispatch(fetchWhatsNewFeed() as any)
    }, [dispatch])

    const lastSeen = getLastSeen()
    const badgeCount = countNew(items, lastSeen)

    const handleClick = () => {
        setLastSeen()
        dispatch(setWhatsNewOpen(true))
    }

    // Hide button entirely when there's nothing to show (empty feed, or error with no items)
    if (!loading && items.length === 0) return null

    return (
        <button
            className="whats-new-btn nav-link"
            onClick={handleClick}
            aria-label={`What's New${badgeCount > 0 ? `, ${badgeCount} new item${badgeCount === 1 ? "" : "s"}` : ""}`}
            title="What's New"
            aria-expanded={isOpen}
        >
            <NewBadgeIcon />
            {badgeCount > 0 && (
                <span className="whats-new-btn__badge" aria-hidden="true">
                    {badgeCount > 99 ? "99+" : badgeCount}
                </span>
            )}
        </button>
    )
}
